/**
 * Vitis product information section (`<vitis-product-information>`).
 *
 * Handles:
 * - scroll-driven expand/collapse of the stat circle
 * - mobile stats / box media carousels via SnapCarousel (@theme/vitis-snap-carousel)
 * - desktop stat hover grouping
 * - wine-fiche download (blob download for cross-origin CDN URLs)
 *
 * CSS classes toggled on the root element:
 * - `is-image-entered` — bottle/image fade-in on load
 * - `is-expanded` — user scrolled past EXPAND_THRESHOLD
 * - `is-stats-revealed` — staggered stat label reveal after expand
 *
 * Box mode (`vitis-mode--box`) uses `data-circle-active` on mobile slides to
 * drive circle content visibility via per-block CSS (vitis-stat-hover-styles).
 */
import { prefersReducedMotion } from '@theme/utilities';
import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';
import { SnapCarousel } from '@theme/vitis-snap-carousel';

/** Scroll distance (px) past the section top before the stat circle expands. */
const EXPAND_THRESHOLD = 120;

/**
 * Fallback when --stat-reveal-total-ms is missing (base + 5×stagger + duration).
 * Prefer the CSS custom property so Liquid/CSS stay the single source of truth.
 */
const STATS_REVEAL_MS_FALLBACK = 1150;

/**
 * @param {HTMLElement} element
 * @returns {number}
 */
function getStatsRevealMs(element) {
  const raw = getComputedStyle(element).getPropertyValue('--stat-reveal-total-ms').trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : STATS_REVEAL_MS_FALLBACK;
}

class VitisProductInformation extends HTMLElement {
  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {number | null} */
  #scrollFrame = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #statsRevealTimer = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #circleHoverTimer = null;

  /** @type {SnapCarousel | null} */
  #statsCarousel = null;

  /** @type {SnapCarousel | null} */
  #mediaCarousel = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    const isDefault = this.classList.contains('vitis-mode--default');

    this.#bindScrollTarget();
    scrollContainerMediaQuery.addEventListener('change', this.#bindScrollTarget, {
      signal: this.#abortController.signal,
    });
    this.#playImageEnter();

    if (!isDefault) {
      this.#setupStatsCarousel();
      this.#setupMediaCarousel();
      this.#setupDesktopCircleHover();
      this.#setupDownloadButtons();
    }
  }

  disconnectedCallback() {
    this.#unbindScrollTarget();
    this.#statsCarousel?.destroy();
    this.#statsCarousel = null;
    this.#mediaCarousel?.destroy();
    this.#mediaCarousel = null;
    this.#abortController.abort();
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
    if (this.#statsRevealTimer) clearTimeout(this.#statsRevealTimer);
    if (this.#circleHoverTimer) clearTimeout(this.#circleHoverTimer);
  }

  get #isBoxMode() {
    return this.classList.contains('vitis-mode--box');
  }

  /** Triggers the bottle/image entrance animation on first paint. */
  #playImageEnter() {
    if (prefersReducedMotion()) {
      this.classList.add('is-image-entered');
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('is-image-entered');
      });
    });
  }

  // -------------------------------------------------------------------------
  // Scroll expand
  // -------------------------------------------------------------------------

  #bindScrollTarget = () => {
    this.#unbindScrollTarget();
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#requestScrollUpdate, { passive: true });
    this.#requestScrollUpdate();
  };

  #unbindScrollTarget() {
    this.#scrollTarget?.removeEventListener('scroll', this.#requestScrollUpdate);
    this.#scrollTarget = null;
  }

  #requestScrollUpdate = () => {
    if (this.#scrollFrame) return;

    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.#handleScroll();
    });
  };

  /** Toggles is-expanded / is-stats-revealed based on scroll position. */
  #handleScroll = () => {
    const expanded = this.getBoundingClientRect().top <= -EXPAND_THRESHOLD;
    const wasExpanded = this.classList.contains('is-expanded');
    this.classList.toggle('is-expanded', expanded);

    if (!expanded) {
      this.classList.remove('is-stats-revealed');
      if (this.#statsRevealTimer) clearTimeout(this.#statsRevealTimer);
      this.#clearBoxCircleActive();
      return;
    }

    if (wasExpanded) return;

    this.classList.remove('is-stats-revealed');

    if (prefersReducedMotion()) {
      this.classList.add('is-stats-revealed');
      return;
    }

    if (this.#statsRevealTimer) clearTimeout(this.#statsRevealTimer);
    const statsRevealMs = getStatsRevealMs(this);
    this.#statsRevealTimer = setTimeout(() => {
      if (this.classList.contains('is-expanded')) {
        this.classList.add('is-stats-revealed');
      }
    }, statsRevealMs);
  };

  // -------------------------------------------------------------------------
  // Mobile stats carousel
  // -------------------------------------------------------------------------

  #setupStatsCarousel() {
    const statsMobile = this.querySelector('.vitis-product__stats-mobile');
    if (!(statsMobile instanceof HTMLElement)) return;

    this.#statsCarousel = new SnapCarousel(statsMobile, {
      onChange: (index, slide) => this.#activateStatSlide(slide),
    });

    // Tapping a chip navigates to it. In box mode this also engages the
    // circle even when the chip is already centered (initially none is
    // engaged so the hint overlay stays visible).
    statsMobile.addEventListener(
      'click',
      (event) => {
        const slide =
          event.target instanceof Element ? event.target.closest('.vitis-product__stats-slide') : null;
        if (!(slide instanceof HTMLElement) || !this.#statsCarousel) return;

        this.#statsCarousel.goTo(this.#statsCarousel.slides.indexOf(slide));
      },
      { signal: this.#abortController.signal }
    );

    this.#syncStatsEndState();
  }

  /**
   * Marks a slide active: chip highlight (`data-active`) and, in box mode,
   * circle reveal (`data-circle-active`). Circle visibility itself is CSS,
   * driven by per-block rules from vitis-stat-hover-styles.
   * @param {HTMLElement} activeSlide
   */
  #activateStatSlide(activeSlide) {
    const isBox = this.#isBoxMode;

    this.querySelectorAll('.vitis-product__stats-slide').forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;
      const isActive = slide === activeSlide;
      slide.dataset.active = String(isActive);
      if (isBox) slide.dataset.circleActive = String(isActive);
    });

    this.#syncStatsEndState();
  }

  #syncStatsEndState() {
    if (!this.#statsCarousel) return;
    const count = this.#statsCarousel.slides.length;
    this.classList.toggle('is-mobile-stats-end', count > 0 && this.#statsCarousel.activeIndex >= count - 1);
  }

  /** Resets box circle-active state when the section collapses on scroll up. */
  #clearBoxCircleActive() {
    if (!this.#isBoxMode) return;

    this.querySelectorAll('.vitis-product__stats-slide').forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;
      slide.dataset.circleActive = 'false';
    });
  }

  // -------------------------------------------------------------------------
  // Box media carousel
  // -------------------------------------------------------------------------

  #setupMediaCarousel() {
    const media = this.querySelector('.vitis-product__media');
    if (!(media instanceof HTMLElement)) return;

    this.#mediaCarousel = new SnapCarousel(media);
  }

  // -------------------------------------------------------------------------
  // Wine desktop stat hover
  // -------------------------------------------------------------------------

  /** Keeps the active stat circle open while moving between stat label and circle content. */
  #setupDesktopCircleHover() {
    const desktopHover = window.matchMedia('(min-width: 990px) and (hover: hover)');
    if (!desktopHover.matches) return;

    const clearActiveCircle = () => {
      this.querySelectorAll('.is-circle-active').forEach((element) => {
        element.classList.remove('is-circle-active');
      });
    };

    const scheduleClearActiveCircle = () => {
      if (this.#circleHoverTimer) clearTimeout(this.#circleHoverTimer);
      this.#circleHoverTimer = setTimeout(clearActiveCircle, 400);
    };

    const cancelClearActiveCircle = () => {
      if (this.#circleHoverTimer) clearTimeout(this.#circleHoverTimer);
    };

    const activateCircle = (/** @type {HTMLElement} */ stat, /** @type {HTMLElement} */ circle) => {
      cancelClearActiveCircle();
      clearActiveCircle();
      stat.classList.add('is-circle-active');
      circle.classList.add('is-circle-active');
    };

    const isInHoverGroup = (/** @type {HTMLElement[]} */ targets, /** @type {EventTarget | null} */ relatedTarget) => {
      if (!(relatedTarget instanceof Node)) return false;

      return targets.some((element) => element === relatedTarget || element.contains(relatedTarget));
    };

    this.querySelectorAll('.vitis-product__stats--desktop .vitis-product__stat--has-circle').forEach((stat) => {
      if (!(stat instanceof HTMLElement)) return;

      const circleId = stat.getAttribute('aria-describedby');
      const circle = circleId ? this.querySelector(`#${CSS.escape(circleId)}`) : null;
      if (!(circle instanceof HTMLElement)) return;

      const downloads = [...circle.querySelectorAll('.vitis-product__circle-download')].filter(
        (element) => element instanceof HTMLElement
      );
      const hoverTargets = [stat, circle, ...downloads];
      const showCircle = () => activateCircle(stat, circle);
      const hideCircle = (/** @type {MouseEvent} */ event) => {
        if (isInHoverGroup(hoverTargets, event.relatedTarget)) return;
        scheduleClearActiveCircle();
      };
      const hideCircleFocus = (/** @type {FocusEvent} */ event) => {
        if (isInHoverGroup(hoverTargets, event.relatedTarget)) return;
        scheduleClearActiveCircle();
      };

      for (const element of [stat, circle]) {
        element.addEventListener('mouseenter', showCircle);
        element.addEventListener('mouseleave', hideCircle);
        element.addEventListener('focusin', showCircle);
        element.addEventListener('focusout', hideCircleFocus);
      }

      downloads.forEach((download) => {
        download.addEventListener('mouseenter', showCircle);
        download.addEventListener('focusin', showCircle);
      });
    });
  }

  /**
   * Force wine-fiche downloads for cross-origin CDN URLs.
   * Browsers ignore the HTML download attribute off-origin, so we fetch a blob.
   * Same-origin links, modified clicks, and no-JS keep native anchor behavior.
   */
  #setupDownloadButtons() {
    const { signal } = this.#abortController;

    this.querySelectorAll('.vitis-product__circle-download').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;

      link.addEventListener(
        'click',
        (event) => {
          void this.#forceDownload(event, link);
        },
        { signal }
      );
    });
  }

  /**
   * @param {MouseEvent} event
   * @param {HTMLAnchorElement} link
   */
  async #forceDownload(event, link) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    let parsedUrl;
    try {
      parsedUrl = new URL(link.href, window.location.href);
    } catch {
      return;
    }

    // Same-origin: the native download attribute is enough.
    if (parsedUrl.origin === window.location.origin) return;

    event.preventDefault();

    if (link.getAttribute('aria-busy') === 'true') return;
    link.setAttribute('aria-busy', 'true');

    const filename =
      link.getAttribute('download')?.trim() || this.#filenameFromUrl(parsedUrl.href) || 'wijn-fiche';
    const { signal } = this.#abortController;

    try {
      const response = await fetch(parsedUrl.href, {
        mode: 'cors',
        credentials: 'omit',
        signal,
      });
      if (!response.ok) throw new Error(`Download failed (${response.status})`);

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      try {
        const temp = document.createElement('a');
        temp.href = objectUrl;
        temp.download = filename;
        temp.rel = 'noopener';
        document.body.append(temp);
        temp.click();
        temp.remove();
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      // CORS / network failure — open the file (no-JS / native fallback path).
      const temp = document.createElement('a');
      temp.href = parsedUrl.href;
      temp.target = '_blank';
      temp.rel = 'noopener noreferrer';
      document.body.append(temp);
      temp.click();
      temp.remove();
    } finally {
      link.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  #filenameFromUrl(url) {
    try {
      const path = new URL(url, window.location.origin).pathname;
      const name = path.split('/').pop() || '';
      return decodeURIComponent(name);
    } catch {
      return '';
    }
  }
}

if (!customElements.get('vitis-product-information')) {
  customElements.define('vitis-product-information', VitisProductInformation);
}
